<template>
  <FormContainer
    withoutDivider
    style="margin-top: -20px"
  >
    <!--
    <template #title>Demandes de remboursement de la proforma</template>
    -->
    <template #content>
      <basic-alert
        v-if="meta?.remboursements?.$messageErreurDirty"
        :alertLabel="meta?.remboursements?.$messageErreurDirty"
        alert-type="danger"
      />
      <basic-alert
        v-else
        alert-label="Veuillez ajouter une ou plusieurs demandes de remboursement"
        alert-type="info"
      />
      <div
        class="form__bloc tableau"
        v-if="model?.remboursements?.length"
      >
        <div class="gridWrapper col-36">
          <div class="cardLayoutWrapper cardLayoutWrapper--header">
            <div class="elem elem--span-4">
              <span>N° DR</span>
            </div>
            <div class="elem elem--span-4">
              <span>Dispositif</span>
            </div>
            <div class="elem elem--span-4">
              <span>N° dossier</span>
            </div>
            <div class="elem elem--span-5">
              <span>Référence interne</span>
            </div>
            <div class="elem elem--span-7">
              <span>Dates</span>
            </div>
            <div class="elem elem--span-6">
              <span>Etablissement</span>
            </div>
            <div class="elem elem--span-4">
              <span>Montant HT</span>
            </div>
            <div class="elem elem--span-2"></div>
          </div>
          <div
            v-for="item in model!.remboursements!"
            :key="item.code"
            class="gridElemWrapper"
          >
            <div class="cardLayoutWrapper">
              <div class="elem elem--span-4">
                <span>{{ item.code }}</span>
              </div>
              <div class="elem elem--span-4">
                <span>{{ item.dispositif }}</span>
              </div>
              <div class="elem elem--span-4">
                <span>{{ item.codeDossier || '-' }}</span>
              </div>
              <div class="elem elem--span-5">
                <span>{{ item.reference || '-' }}</span>
              </div>
              <div class="elem elem--span-7">
                <span>-</span>
              </div>
              <div class="elem elem--span-6">
                <span>{{ item.etablissementEmetteur?.denomination || '-' }}</span>
              </div>
              <div class="elem elem--span-4 elem--right">
                <span>{{ formatNumber(item.montantHt, { decimal: 2 }) ?? '-' }}</span>
              </div>
              <div
                v-if="!readonly"
                class="elem elem--span-2 elem--right"
              >
                <BasicTooltip label="Supprimer">
                  <BasicIcon
                    name="trash"
                    @click="removeDRFromList(item)"
                  />
                </BasicTooltip>
              </div>
            </div>
          </div>
        </div>
      </div>
      <EmptyDataTable v-else>
        <template #content>
          Aucune
          <BasicText>demande de remboursement</BasicText>
          pour le moment
        </template>
      </EmptyDataTable>
      <!-- Mobile -->
      <div
        class="block-mobile"
        v-if="model?.remboursements?.length"
      >
        <div
          v-for="item in model!.remboursements!"
          :key="item.code"
          class="mobileCardWrapper"
        >
          <div class="header">
            <div class="headerContent">
              <span class="headerTitle">{{ item.dispositif }}</span>
              <span class="headerSubtitle">{{ item.code }}</span>
              <span
                class="headerSubtitle"
                v-if="item.reference"
              >
                {{ item.reference }}
              </span>
            </div>
            <div
              v-if="!readonly"
              class="actionsWrapper"
            >
              <BasicTooltip label="Supprimer">
                <BasicIcon
                  name="trash"
                  @click="removeDRFromList(item)"
                />
              </BasicTooltip>
            </div>
          </div>
          <div class="content">
            <div class="contentElement">
              <BasicIcon name="bank" />
              <span>{{ item.etablissementEmetteur?.denomination || '-' }}</span>
            </div>
            <div class="contentElement">
              <BasicIcon name="document-text" />
              <span>N° dossier : {{ item.codeDossier || '-' }}</span>
            </div>
          </div>
          <div class="footer">
            <div class="footerElem">
              <span class="footerTitle">Montant HT</span>
              <span class="footerSubtitle">{{ formatNumber(item.montantHt, { decimal: 2 }) ?? '-' }} €</span>
            </div>
          </div>
        </div>
      </div>
      <div class="form__bloc right totaux-bloc">
        <WrapperInput
          id=""
          label="Total HT"
          class="col-9 col-md-12 col-sm-36"
          :disabled="true"
          :modelValue="formatNumber(totalHT, { decimal: 2 })"
        />
        <WrapperInput
          id=""
          label="Montant TVA"
          class="col-9 col-md-12 col-sm-36"
          :disabled="true"
          :modelValue="formatNumber(montantTVA, { decimal: 2 })"
        />
        <WrapperInput
          id=""
          label="Total TTC"
          class="col-9 col-md-12 col-sm-36"
          :disabled="true"
          :modelValue="formatNumber(totalTTC, { decimal: 2 })"
        />
      </div>
      <div
        v-if="!readonly"
        class="form__bloc"
      >
        <div class="elem elem--span-23"></div>
        <BasicButton
          id=""
          label="Ajouter une demande de remboursement"
          class="col-12 col-md-18 col-sm-36"
          @click="rechercheVisible = true"
        />
      </div>
    </template>
  </FormContainer>
  <RechercheDR v-model="rechercheVisible"></RechercheDR>
</template>

<script setup lang="ts">
  import FormContainer from '@/features/interface/demande/shared/components/FormContainer.vue'
  import RechercheDR from '@/features/interface/demande/proforma/recherche-dr/RechercheDR.vue'
  import { ref, computed } from 'vue'
  import { storeToRefs } from 'pinia'
  import { DEMANDE_STORE } from '@/features/interface/demande/shared/components'
  import { inject } from 'vue'
  import { formatNumber } from '@/features/shared/tools'
  import type { RemboursementProformaDto } from '@/features/api/extranet/dto/Demande/PROFORMA'
  import EmptyDataTable from '../../../shared/components/EmptyDataTable.vue'

  const store = inject(DEMANDE_STORE)!.getStore('MODEL_PROFORMA')
  const { model, meta, readonly } = storeToRefs(store)

  const rechercheVisible = ref<boolean>(false)

  const removeDRFromList = store.setupCustomUpdater<RemboursementProformaDto>((patch, item) => {
    patch.remboursements = patch.remboursements.filter((rmb) => rmb.code !== item.code)
  })

  const totalHT = computed(() => {
    return model.value?.remboursements.reduce((sum, module) => sum + (module.montantHt ?? 0), 0)
  })

  const montantTVA = computed(() => {
    return model.value?.remboursements[0]?.montantTva ?? 0
  })

  const totalTTC = computed(() => {
    return model?.value?.remboursements.reduce((sum, module) => sum + (module.montantHt + module.montantTva), 0)
  })
</script>

<style lang="less" scoped>
  .right {
    display: flex;
    justify-content: end;
    align-items: end;
  }

  .totaux-bloc {
    margin-left: 45%;
  }

  svg {
    margin-left: auto;
    fill: @persian-600;
    transform: scale(0.9) !important;
    overflow: visible;
    cursor: pointer;
  }

  .block-mobile {
    display: none;
  }

  .elem .input-container > * {
    justify-items: end !important;
  }

  .form {
    &__bloc {
      grid-template-columns: repeat(35, 1fr);
    }
  }

  .addButton {
    display: flex;
    justify-content: flex-end;
    margin-top: -70px;
    z-index: 10;
  }

  /* --- Tablette et Mobile --- */
  @container (max-width: @breakpoint-tablet) {
    .right {
      display: flex;
      justify-content: end;
      align-items: end;
    }

    .totaux-bloc {
      margin-left: 0;
    }

    .addButton {
      display: block;
      margin-top: -20px;
      z-index: unset;
    }

    .tableau {
      display: none;
    }

    .block-mobile {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 16px;
    }

    .mobileCardWrapper {
      display: flex;
      height: 100%;
      border-radius: 8px;
      border: 1px solid @neutral-200;
      overflow: hidden;
      flex-direction: column;
    }

    .header {
      padding: 16px;
      display: flex;
      justify-content: space-between;
      background: @light-blue;

      svg {
        fill: @persian-600;
        transform: scale(0.8);
        cursor: pointer;
      }
    }

    .headerContent {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 6px;
    }

    .headerTitle {
      color: @neutral-500;
      font-size: @font-size-body-m;
      font-weight: @font-weight-semibold;
    }

    .headerSubtitle {
      color: @secondary-1000;
      font-size: @font-size-body-l;
      font-weight: @font-weight-bold;
    }

    .actionsWrapper {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .content {
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 12px;
      flex: 1;
    }

    .contentElement {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      color: @secondary-1000;
      font-size: @font-size-body-m;
      font-weight: @font-weight-semibold;

      svg {
        fill: #1f747f;
        transform: scale(0.8);
        min-width: 24px;
        margin-left: 0;
      }

      span {
        margin-left: 8px;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    .footer {
      padding: 16px;
      background: @light-blue;
      border-top: 1px solid @neutral-200;
    }

    .footerElem {
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .footerTitle {
      color: @neutral-500;
      font-size: 10px;
      font-weight: @font-weight-bold;
    }

    .footerSubtitle {
      color: @secondary-1000;
      font-size: @font-size-body-m;
      font-weight: @font-weight-semibold;
    }
  }

  @container (max-width: @breakpoint-mobile) {
    .block-mobile {
      grid-template-columns: repeat(1, 1fr);
    }
  }
</style>
